import { useTranslation } from 'react-i18next';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ChevronDown, ChevronUp, ClipboardList, GripVertical, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { TYPE } from '@/lib/typography';
import { cn } from '@/utils/cn';

export function SortableStepItem({
  index,
  stepCount,
  selected,
  title,
  serviceName,
  duration,
  interval,
  allowedCount,
  hasError,
  canEdit,
  clientId,
  onSelect,
  onMoveUp,
  onMoveDown,
  onRemove,
}: {
  index: number;
  stepCount: number;
  selected: boolean;
  title: string;
  serviceName: string;
  duration: string;
  interval: string | null;
  allowedCount: number;
  hasError: boolean;
  canEdit: boolean;
  clientId: string;
  onSelect: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onRemove: () => void;
}) {
  const { t } = useTranslation();
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id: clientId,
    disabled: !canEdit,
  });
  const questionnaireLabel =
    allowedCount > 0
      ? t('treatment_courses.allowed_step_count', { count: allowedCount })
      : t('treatment_courses.allowed_free_choice');

  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className="relative"
    >
      {index < stepCount - 1 ? (
        <span
          className="absolute left-[1.15rem] top-10 h-[calc(100%-0.5rem)] w-px bg-ds-line"
          aria-hidden
        />
      ) : null}
      <div
        data-testid={`treatment-course-selection-step-${clientId}`}
        className={cn(
          'flex items-start gap-s2 rounded-ctl border px-s2 py-s2 transition-colors',
          selected
            ? 'border-ds-teal bg-ds-teal-tint'
            : hasError
              ? 'border-ds-red-ink/40 bg-ds-card'
              : 'border-ds-line bg-ds-card hover:bg-ds-neutral-tint',
        )}
      >
        {canEdit ? (
          <button
            type="button"
            className="mt-1 cursor-grab touch-none text-ds-muted hover:text-ds-text"
            aria-label={t('treatment_courses.drag_step')}
            {...attributes}
            {...listeners}
          >
            <GripVertical className="h-4 w-4" />
          </button>
        ) : (
          <span className="mt-1 h-4 w-4" aria-hidden />
        )}
        <button
          type="button"
          onClick={onSelect}
          className="min-w-0 flex-1 overflow-visible text-left"
        >
          <span className={cn(TYPE.control, 'block font-semibold text-ds-text')}>{title}</span>
          <span className={cn(TYPE.small, 'mt-0.5 block text-ds-text')}>{serviceName}</span>
          <span className={cn(TYPE.small, 'mt-0.5 block text-ds-muted')}>
            {[duration, interval].filter(Boolean).join(' · ')}
          </span>
          <span
            className={cn(
              'sk stp-q w-max min-w-max shrink-0 whitespace-nowrap',
              allowedCount === 0 && 'free',
            )}
            data-testid={`treatment-course-step-questionnaires-${clientId}`}
          >
            <ClipboardList className="h-3 w-3 shrink-0" aria-hidden />
            {questionnaireLabel}
          </span>
          {hasError ? (
            <span className={cn(TYPE.small, 'mt-0.5 block font-medium text-ds-red-ink')}>
              {t('treatment_courses.step_error')}
            </span>
          ) : null}
        </button>
        {canEdit ? (
          <div className="flex shrink-0 items-center gap-s1">
            <Button
              type="button"
              variant="tertiary"
              size="icon"
              onClick={onMoveUp}
              disabled={index === 0}
              aria-label={t('treatment_courses.move_step_up')}
            >
              <ChevronUp />
            </Button>
            <Button
              type="button"
              variant="tertiary"
              size="icon"
              onClick={onMoveDown}
              disabled={index === stepCount - 1}
              aria-label={t('treatment_courses.move_step_down')}
            >
              <ChevronDown />
            </Button>
            <Button
              type="button"
              variant="danger"
              size="icon"
              onClick={onRemove}
              disabled={stepCount <= 1}
              aria-label={t('treatment_courses.remove_step')}
            >
              <Trash2 />
            </Button>
          </div>
        ) : null}
      </div>
    </li>
  );
}
